import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { getEnrolledEmail, isBiometricEnrolled, refreshStoredToken } from 'services/nativeBiometric';
import { exchangeNativeHandoffCode, exchangeRefreshToken } from 'services/nativeLogin';
import { markBiometricEnrollmentOffer } from 'hooks/useBiometricEnrollmentOffer';
import { consumeLoginReturnPath, storeAuthTokens } from 'utils/auth';
import { fetchGameCloseMaintenance, MaintenanceInfo } from 'utils/maintenance';

// Hard ceiling for the whole code-exchange -> reload sequence. Every step below
// is individually bounded, but a native call (keystore) or a future step that
// is not must not be able to strand the player on the splash: past this the
// attempt is abandoned and the failure is shown.
const NATIVE_LOGIN_WATCHDOG_MS = 45_000;
// The keystore re-arm is best effort (a failure only means biometric sign-in
// needs re-enrolling), so it gets a short bound instead of blocking the login.
const REARM_TIMEOUT_MS = 5_000;

export type NativeLoginFailure = { title: string; displayMessage: string };

const SIGN_IN_FAILED: NativeLoginFailure = {
  title: 'Sign-in failed',
  displayMessage: "We couldn't complete your sign-in. Please try again."
};

const withTimeout = <T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(fallback);
      }
    );
  });

export type NativeLoginDeepLinkState = {
  loginInProgress: boolean;
  // Set when a deep-link login could not be completed (rejected or unreachable
  // code, or a step stalled). The caller renders it instead of leaving the
  // player on the guest lobby with no explanation.
  loginFailure: NativeLoginFailure | null;
  clearLoginFailure: () => void;
  // Set instead of completing the login when the game-close maintenance window
  // is active (see fetchGameCloseMaintenance) — the caller should render
  // MaintenanceNotice with this and clearMaintenanceInfo instead of proceeding.
  maintenanceInfo: MaintenanceInfo | null;
  clearMaintenanceInfo: () => void;
};

/**
 * App-level handler for the native login deep-link callback.
 *
 * On native, login opens the auth app's `/loginapp` page in the system browser
 * (see `redirectToLogin`), which redirects back to
 * `hijackpoker://account?refresh_token=…`. Because a guest can launch that flow
 * directly from a CTA (without routing through the in-app LoginScreen), this
 * listener MUST live at the app root so the callback is always handled.
 *
 * It exchanges the refresh token for a session via the engine, gates on the
 * game-close maintenance check (Unity parity — same gate LoginScreen's web
 * flow applies; fails open, see the util), then stores the session and
 * hard-reloads into the member app. The reload is deterministic — it always
 * re-inits sockets/Redux cleanly from the stored token — and the returned
 * `loginInProgress` lets App show the shared SplashScreen so the reload is
 * masked (login splash → member lobby splash, no guest-lobby flash between).
 *
 * (An earlier version reconnected in place to avoid the reload's brief blank
 * frame, but on flaky-update-socket envs it wasn't reliable; the masked reload
 * is simpler and always works.)
 *
 * No-op on web (the embedded Descope flow handles its own success).
 */
export function useNativeLoginDeepLink(): NativeLoginDeepLinkState {
  const [loginInProgress, setLoginInProgress] = useState(false);
  const [maintenanceInfo, setMaintenanceInfo] = useState<MaintenanceInfo | null>(null);
  const [loginFailure, setLoginFailure] = useState<NativeLoginFailure | null>(null);
  // The same link can reach the app twice (iOS may deliver the automatic
  // redirect late AND the player taps the fallback); one attempt at a time.
  const attemptInFlightRef = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let attemptStartedAt = 0;
    // Bumped when an attempt is abandoned (watchdog or failure) or a new one starts.
    // A continuation from an older attempt that settles late checks this and stops,
    // so it cannot store tokens or reload over a failure already shown or a newer login.
    let attemptSeq = 0;
    let watchdog: ReturnType<typeof setTimeout> | undefined;
    // Timing per step, so a slow or stalled attempt can be diagnosed from the
    // WebView console. Contains no codes, tokens or identity.
    const step = (name: string) =>
      console.info(`[NativeLogin] ${name} (+${Date.now() - attemptStartedAt}ms)`);
    const endAttempt = () => {
      attemptInFlightRef.current = false;
      if (watchdog) clearTimeout(watchdog);
      watchdog = undefined;
    };
    const failAttempt = (reason: string) => {
      step(`failed: ${reason}`);
      attemptSeq += 1;
      endAttempt();
      setLoginInProgress(false);
      Browser.close().catch(() => {});
      // The reason slug (a fixed set, no identity) is shown so a failed attempt on
      // a device can be read off a screenshot without a console or Sentry.
      setLoginFailure({
        ...SIGN_IN_FAILED,
        displayMessage: `${SIGN_IN_FAILED.displayMessage} (${reason})`
      });
      Sentry.captureMessage('native login handoff failed', {
        level: 'warning',
        tags: { reason }
      });
    };

    const complete = async (seq: number, sessionJwt: string, refreshJwt: string) => {
      // The engine's exchange rotates the refresh token, which invalidates the
      // copy sitting in the biometric keystore. Without this re-arm, biometric
      // login works exactly once and then fails on a dead token. Must be
      // awaited BEFORE the reload below: the reload tears down the WebView,
      // and an un-awaited native keystore write racing that teardown can be
      // cut off mid-write, stranding the keystore on the now-invalidated
      // token. A failed re-arm must not block this otherwise-successful
      // login — it's logged so it can be diagnosed, not retried here, since
      // the next successful login re-arms it anyway.
      const rearm = await withTimeout(
        refreshStoredToken(getEnrolledEmail() || '', refreshJwt),
        REARM_TIMEOUT_MS,
        { ok: false, error: 'timeout' }
      );
      if (seq !== attemptSeq) return;
      step('keystore re-armed');
      if (!rearm.ok) {
        console.warn('[NativeLogin] failed to re-arm biometric keystore after login:', rearm.error);
      }
      // Offer enrollment on the other side of the reload below — a modal opened
      // here would be torn down before the player ever saw it (AC2).
      if (!isBiometricEnrolled()) markBiometricEnrollmentOffer();
      storeAuthTokens(sessionJwt, refreshJwt);
      Browser.close().catch(() => {});
      step('tokens stored, reloading into member app');
      endAttempt();
      // Reload straight into the member app, back to whatever page the guest
      // launched login from. The loginInProgress SplashScreen covers this; on
      // boot the member lobby shows its own splash until ready.
      window.location.replace(window.location.origin + consumeLoginReturnPath());
    };

    // Checked BEFORE storing tokens/reloading — a maintenance window blocks
    // entry the same way LoginScreen's web flow blocks it, so nothing is
    // persisted for the "Back to Login" case to undo.
    const proceedUnlessMaintenance = async (seq: number, sessionJwt: string, refreshJwt: string) => {
      const { active, info } = await fetchGameCloseMaintenance(sessionJwt);
      if (seq !== attemptSeq) return;
      step('maintenance checked');
      if (active) {
        console.log('[NativeLogin] game-close maintenance active, blocking entry');
        endAttempt();
        setLoginInProgress(false);
        Browser.close().catch(() => {});
        setMaintenanceInfo(info ?? {});
        return;
      }
      await complete(seq, sessionJwt, refreshJwt);
    };

    const handler = CapacitorApp.addListener('appUrlOpen', (data: { url: string }) => {
      console.log('[NativeLogin] appUrlOpen', data?.url?.substring(0, 40));
      if (!data?.url?.startsWith('hijackpoker://account')) return;

      const params = new URLSearchParams(data.url.split('?')[1] || '');
      // `code` (EN-5415) is the normal case — apps/auth mints a short-lived,
      // single-use code instead of putting the raw refresh token in this
      // deep link, since a custom URL scheme is not a verified recipient.
      // `refresh_token` is still accepted so this handler keeps working
      // during a rollout window against an older/degraded auth build (its
      // own mint call falls back to the raw token if the engine is
      // unreachable) — never require BOTH sides to have deployed atomically.
      const code = params.get('code');
      const refreshToken = params.get('refresh_token');
      if (!code && !refreshToken) {
        console.warn('[NativeLogin] callback had neither a code nor a refresh_token');
        return;
      }

      if (attemptInFlightRef.current) {
        console.info('[NativeLogin] deep link ignored: a login attempt is already in progress');
        return;
      }
      attemptInFlightRef.current = true;
      attemptSeq += 1;
      const seq = attemptSeq;
      attemptStartedAt = Date.now();
      setLoginFailure(null);
      watchdog = setTimeout(() => failAttempt('watchdog'), NATIVE_LOGIN_WATCHDOG_MS);

      // Cover the guest lobby immediately — before the async exchange — so
      // closing the browser reveals the splash, not the guest lobby.
      setLoginInProgress(true);
      step('deep link received');
      // The in-app browser is still on top of the app. Close it now so the splash
      // is what the player sees; otherwise the page stays up with its tappable
      // link while the exchange runs, and a second tap can only find a spent code.
      Browser.close().catch(() => {});

      // Exchange the code/refresh token for a full session via the engine
      // (same hardened exchange the biometric-unlock path uses — retries
      // transient network failures, but a definitive rejection means the
      // credential is dead). Never fall back to treating the raw refresh
      // token as an access token: it was never verified, so proceeding with
      // it would let a native app that intercepted the redirect (or a
      // stale/forged token) straight in.
      (code ? exchangeNativeHandoffCode(code) : exchangeRefreshToken(refreshToken as string))
        .then((exchanged) => {
          if (seq !== attemptSeq) return;
          if (!exchanged.ok) {
            failAttempt(`exchange-${exchanged.reason}`);
            return;
          }
          step('code exchanged');
          return proceedUnlessMaintenance(seq, exchanged.sessionJwt, exchanged.refreshJwt);
        })
        .catch((e) => {
          if (seq !== attemptSeq) return;
          console.warn('[NativeLogin] login completion threw:', e?.message || e);
          failAttempt('exception');
        });
    });

    return () => {
      endAttempt();
      handler.then((h) => h.remove()).catch(() => {});
    };
  }, []);

  const clearMaintenanceInfo = useCallback(() => setMaintenanceInfo(null), []);
  const clearLoginFailure = useCallback(() => setLoginFailure(null), []);

  return {
    loginInProgress,
    loginFailure,
    clearLoginFailure,
    maintenanceInfo,
    clearMaintenanceInfo
  };
}
